import { useEffect, useMemo, useState } from "react";
import debounce from "lodash/debounce";
import { OptionRenderType } from "controls/global/stewart-checkbox/StewartAutocompleteWithCheckboxes";

export type AsyncTextFilterState = {
  options: OptionRenderType[];
  loading: boolean;
  onInputChange: (text: string) => void;
};

// Generic debounced typeahead for endpoints that return string[]. The hook
// owns its own options/loading state and a 350 ms debounced input handler.
// Min-length-1 guard skips empty/whitespace-only input only.
// Cancels in-flight debounced calls on unmount.
export function useAsyncTextFilter(
  fetcher: (text: string) => Promise<string[]>
): AsyncTextFilterState {
  const [options, setOptions] = useState<OptionRenderType[]>([]);
  const [loading, setLoading] = useState(false);
  const onInputChange = useMemo(
    () =>
      debounce(async (text: string) => {
        if (text.trim().length < 1) {
          setOptions([]);
          return;
        }
        setLoading(true);
        const values = await fetcher(text);
        setLoading(false);
        setOptions(values.map((v) => ({ label: v, value: v })));
      }, 350),
    [fetcher]
  );
  useEffect(() => () => onInputChange.cancel(), [onInputChange]);
  return { options, loading, onInputChange };
}

// Same shape as useAsyncTextFilter but for fetchers that already return
// fully-formed OptionRenderType[] (label ≠ value). No internal mapping.
export function useAsyncOptionFilter(
  fetcher: (text: string) => Promise<OptionRenderType[]>
): AsyncTextFilterState {
  const [options, setOptions] = useState<OptionRenderType[]>([]);
  const [loading, setLoading] = useState(false);
  const onInputChange = useMemo(
    () =>
      debounce(async (text: string) => {
        if (text.trim().length < 1) {
          setOptions([]);
          return;
        }
        setLoading(true);
        const result = await fetcher(text);
        setLoading(false);
        setOptions(result);
      }, 350),
    [fetcher]
  );
  useEffect(() => () => onInputChange.cancel(), [onInputChange]);
  return { options, loading, onInputChange };
}
